import { useEffect } from "react";

const DEFAULT_INTERACTIVE_SELECTOR =
  "button, a, input, textarea, select, label, [role='button'], [data-touch-ignore]";

const DEFAULT_POINTER_TYPES = ["touch", "pen"];

/**
 * Adds simple tap navigation for touch devices. Tapping the left or right side
 * of the referenced element triggers the provided callbacks. Optionally a
 * centre tap can toggle controls or any other behaviour.
 */
export default function useTouchNavigation(ref, options = {}) {
  const {
    onLeft,
    onRight,
    onCenter,
    threshold = 0.35,
    pointerTypes = DEFAULT_POINTER_TYPES,
    ignoreInteractive = true,
  } = options;

  useEffect(() => {
    const element = ref?.current;
    if (!element || typeof onLeft !== "function" || typeof onRight !== "function") {
      return undefined;
    }

    const pointerTypesSet =
      Array.isArray(pointerTypes) && pointerTypes.length
        ? new Set(pointerTypes)
        : null;

    const resolvePoint = (event) => {
      if (event.type.startsWith("pointer")) {
        const type = event.pointerType || "mouse";
        return {
          clientX: event.clientX,
          type,
        };
      }

      if (event.type.startsWith("touch") && event.changedTouches?.length) {
        const touch = event.changedTouches[0];
        return {
          clientX: touch.clientX,
          type: "touch",
        };
      }

      return null;
    };

    const handleTap = (event) => {
      const point = resolvePoint(event);
      if (!point || typeof point.clientX !== "number") return;

      if (pointerTypesSet && !pointerTypesSet.has(point.type)) {
        return;
      }

      if (ignoreInteractive) {
        const target = event.target;
        if (
          target?.closest &&
          target.closest(DEFAULT_INTERACTIVE_SELECTOR)
        ) {
          return;
        }
      }

      const rect = element.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;

      const ratio = (point.clientX - rect.left) / rect.width;
      if (Number.isNaN(ratio)) return;

      let handled = false;
      if (ratio <= threshold) {
        onLeft();
        handled = true;
      } else if (ratio >= 1 - threshold) {
        onRight();
        handled = true;
      } else if (typeof onCenter === "function") {
        onCenter();
        handled = true;
      }

      if (handled) {
        event.preventDefault?.();
        event.stopPropagation?.();
      }
    };

    const supportsPointerEvents =
      typeof window !== "undefined" && "PointerEvent" in window;

    if (supportsPointerEvents) {
      element.addEventListener("pointerup", handleTap, { passive: false });
    } else {
      element.addEventListener("touchend", handleTap, { passive: false });
    }

    return () => {
      if (supportsPointerEvents) {
        element.removeEventListener("pointerup", handleTap);
      } else {
        element.removeEventListener("touchend", handleTap);
      }
    };
  }, [ref, onLeft, onRight, onCenter, threshold, pointerTypes, ignoreInteractive]);
}
